import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { PettyCash } from '../../types';
import Button from '../Form/Button';
import TextArea from '../Form/TextArea';
import { generatePettyCashTemplate } from './PettyCashLetterTemplate';

interface PettyCashLetterGeneratorProps {
  pettyCash: PettyCash;
  onClose: () => void;
  onGenerate: (data: PettyCashLetterData) => void;
}

interface PettyCashLetterData {
  pettyCashId: string;
  customContent?: string;
  notes?: string;
}

const PettyCashLetterGenerator: React.FC<PettyCashLetterGeneratorProps> = ({
  pettyCash,
  onClose,
  onGenerate
}) => {
  const [previewContent, setPreviewContent] = useState('');

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<PettyCashLetterData>({
    defaultValues: {
      pettyCashId: pettyCash.id,
      customContent: '',
      notes: ''
    }
  });

  useEffect(() => {
    const template = generatePettyCashTemplate(pettyCash);
    setPreviewContent(template);
  }, [pettyCash]);

  const handleFormSubmit = (data: PettyCashLetterData) => {
    onGenerate(data);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(previewContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleDownload = () => {
    const blob = new Blob([previewContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `petty-cash-${pettyCash.employeeName}-${pettyCash.date}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-lg">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Generate Petty Cash Statement
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              كشف العهدة المالية اليومي - {pettyCash.employeeName} - {new Date(pettyCash.date).toLocaleDateString()}
            </p>
          </div>
          <div className="flex space-x-2">
            <Button
              type="button"
              variant="primary"
              onClick={handlePrint}
            >
              Print Report
            </Button>
            <Button
              type="button"
              variant="success"
              onClick={handleDownload}
            >
              Download HTML
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Preview Section */}
        <div className="flex-1 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
          <div 
            className="border border-gray-300 rounded-lg p-4 bg-white shadow-sm overflow-auto"
            style={{ height: '800px' }}
            dangerouslySetInnerHTML={{ __html: previewContent }}
          />
        </div>

        {/* Options Section */}
        <div className="w-80 border-l border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Options</h3>
          
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            <div>
              <TextArea
                label="Additional Notes"
                {...register('notes')}
                placeholder="Add any additional notes or comments..."
                rows={4}
              />
            </div>

            <div>
              <TextArea
                label="Custom Content"
                {...register('customContent')}
                placeholder="Add custom content to include in the report..."
                rows={4}
              />
            </div>

            <div className="pt-4 border-t border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Report Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Employee:</span>
                  <span className="font-medium">{pettyCash.employeeName}</span>
                </div>
                <div className="flex justify-between">
                  <span>Date:</span>
                  <span className="font-medium">{new Date(pettyCash.date).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Location:</span>
                  <span className="font-medium">{pettyCash.location}</span>
                </div>
                <div className="flex justify-between">
                  <span>Expenses Count:</span>
                  <span className="font-medium">{pettyCash.expenses?.length || 0}</span>
                </div>
                <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                  <span>Final Balance:</span>
                  <span className={pettyCash.finalBalance >= 0 ? 'text-green-600' : 'text-red-600'}>
                    ${pettyCash.finalBalance.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col space-y-2 pt-4">
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Generating...' : 'Generate Final Report'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PettyCashLetterGenerator;
